import { FLYER_HEIGHT, roadY } from "./common";
import { Death, EnemyView } from "./enemyView";
import { audio } from "../../audio/audio";
import { WYRM_CUE } from "../../audio/wyrmCues";

/**
 * The Starfall Wyrm's own clips on its pooled view (M40; art/unitkit/README.md "Starfall Wyrm"): it dives in with
 * `spawn` (no boss rise out of the ground), roars with `roar`, falls with its long `die` (a spiral and a crash at 1.3 s,
 * its own — not the flyer's quick spiral) and, when it gets away, soars up and out of frame with `flee`, held on its
 * last frame while the body dissolves into the sky. EnemyViews (enemies.ts) calls these; enemyDeath.ts steps the fall
 * and the flee. Each clip is played `solo` so the flight loop under it does not average half of it away.
 */
export const WYRM_CLIPS = {
  /** The dive in (s), its great downbeat and its roar: the moment's slam lands on the downbeat (ui/wyrmModel.ts). */
  spawn: 2.53, downbeat: 1.0,
  roar: 1.63,
  /** The flee: it is out of frame by its end; the body dissolves over the last part (a clamp, then gone). */
  flee: 1.83, fleeDissolveAt: 1.35, fleeDissolveFor: 0.45,
  /**
   * The fall: it crashes at 1.3 s and bursts (wyrmDeath.ts); its body dissolves through the burst from the crash, a
   * little slower than a boss's (the explosion is the moment).
   */
  die: 1.63, crash: 1.3, fallDissolveAt: 1.3, fallDissolveFor: 1.1,
  /** Cells it flies over a flyer's height: its belly clears the tallest tower tops at play zoom. */
  lift: 0.75,
  /**
   * Its size on the road over the authored one (2.4 wingspan): it dwarfs a tower. Its plate, bar, ring and tap target
   * follow (they read the view's scale).
   */
  scale: 1.4,
  /** A hit's white flash on its body (1 for every other unit): a stream of hits must never bleach the model. */
  hitFlash: 0.25,
  /** How far its motes orbit (× its height): out past its wingtips, never on its body. */
  moteOrbit: 1.55,
} as const;

/**
 * How high a flyer is drawn over the road: every flyer at FLYER_HEIGHT; the Wyrm higher (`lift`), so the showpiece soars
 * over the tower tops instead of weaving between them, and its plate, bar and tap target ride with it.
 */
export function flyHeight(v: Pick<EnemyView, "air" | "wyrm">): number { return v.air ? FLYER_HEIGHT + (v.wyrm ? WYRM_CLIPS.lift : 0) : 0; }

/** The Wyrm appears (first time or a retry): the dive. */
export function wyrmArrive(v: EnemyView): void { v.rig.playOnce("spawn", false, null, { solo: true }); }

/** A roar (a health threshold, its rally): never over the dive or another roar. */
export function wyrmRoar(v: EnemyView): boolean {
  if (v.rig.busy) return false;
  v.rig.playOnce("roar", false, null, { solo: true });
  return true;
}

/** It got away (WyrmFled): the flee clip, held at its end, while the body fades out high above the road. */
export function wyrmFlee(v: EnemyView): void {
  v.bar.dying = true;
  v.dead = 0;
  v.doomed = false; v.owed = 0;
  v.death = Death.Flee;
  v.rig.playOnce("flee", true);
  v.dissolveAt = WYRM_CLIPS.fleeDissolveAt;
  v.dissolveFor = WYRM_CLIPS.fleeDissolveFor;
}

/** It is slain: its own fall from the air, crashing at 1.3 s into its death burst, dissolving through it. */
export function wyrmFall(v: EnemyView): void {
  crashed.delete(v);
  v.death = Death.Fall;
  v.rig.playOnce("die", true);
  v.fallFrom = v.body.position.y - roadY();
  v.dissolveAt = WYRM_CLIPS.fallDissolveAt;
  v.dissolveFor = WYRM_CLIPS.fallDissolveFor;
}

/**
 * Per frame of the fall: the height it falls from eases to the ground by the crash (the clip spins and rolls it), and on
 * the crash frame its crash sounds — on the clip's own clock, so the kill's slow motion (which stretches the 1.3 s to
 * ~2.3 s of real time) never puts the sound ahead of the picture.
 */
export function wyrmFallStep(v: EnemyView): void {
  const u = Math.min(1, v.dead / WYRM_CLIPS.crash);
  v.body.position.y = roadY() + v.fallFrom * (1 - u * u);
  if (u >= 1 && !crashed.has(v)) { crashed.add(v); audio.play(WYRM_CUE.crash); }
}
/** The falls whose crash has sounded (a pooled view is cleared when it falls again). */
const crashed = new WeakSet<EnemyView>();
